import React, { useState } from "react";
import ModuleHeader from "../components/ModuleHeader";
import AcceptanceChecklist from "../components/AcceptanceChecklist";
import StateMap from "../components/StateMap";
import ContractPanel from "../components/ContractPanel";
import ScenarioSwitcher, { withScenario } from "../components/ScenarioSwitcher";
import AsyncBoundary from "../components/AsyncBoundary";
import AuditMatrix from "../components/AuditMatrix";
import useApiResource from "../hooks/useApiResource";
import useBreakpoint from "../hooks/useBreakpoint";
import useCrossBreakpointAudit from "../hooks/useCrossBreakpointAudit";
import { apiClient } from "../utils/apiClient";
import { mapApiError } from "../utils/errorMapper";
import { evaluateShared } from "../utils/viewportAudit";
import { modules, overviewItem } from "../config/navigation";

const PAGES = [overviewItem, ...modules.filter((m) => !["/responsive-audit", "/sidebar-audit"].includes(m.path))];

function AuditRunner({ breakpoints }) {
  const [path, setPath] = useState("/");
  const evaluate = React.useCallback((m, bp) => evaluateShared(m, bp, { path }), [path]);
  const { results, running, error, run, summary } = useCrossBreakpointAudit({ breakpoints, path, evaluate });

  const [reviewer, setReviewer] = useState("");
  const [notes, setNotes] = useState("");
  const [submit, setSubmit] = useState({ state: "idle", message: "", fieldError: "" });

  const send = async (e) => {
    e.preventDefault();
    if (reviewer.trim().length < 2) {
      setSubmit({ state: "error", message: "", fieldError: "Reviewer name is required (minimum 2 characters)." });
      return;
    }
    setSubmit({ state: "submitting", message: "", fieldError: "" });
    try {
      const flat = Object.entries(results).flatMap(([bp, r]) => (r.checks || []).map((c) => ({ breakpoint: bp, ...c })));
      const res = await apiClient("/api/qa/audit-reports", { method: "POST", body: JSON.stringify({ reviewer, page: path, notes, results: flat }) });
      setSubmit({ state: "success", message: `Report ${res.data.reportId} saved.`, fieldError: "" });
    } catch (err) {
      setSubmit({ state: "error", message: err.status === 422 ? "" : mapApiError(err), fieldError: err.details?.fields?.reviewer || err.details?.fields?.results || "" });
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <label htmlFor="audit-page" className="form-label small mb-1">Page to audit</label>
          <select id="audit-page" className="form-select form-select-sm" value={path} onChange={(e) => setPath(e.target.value)} disabled={running}>
            {PAGES.map((p) => <option key={p.path} value={p.path}>{p.label}</option>)}
          </select>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={run} disabled={running}>
          {running ? "Auditing…" : "Run breakpoint audit"}
        </button>
        {summary.total > 0 && (
          <span className={`badge-soft ${summary.passed === summary.total ? "badge-success" : "badge-danger"}`}>
            {summary.passed}/{summary.total} checks passed
          </span>
        )}
      </div>
      {error && <div className="alert alert-danger small" role="alert">{error.message}</div>}
      <AuditMatrix breakpoints={breakpoints} results={results} />

      {summary.finished && (
        <form className="demo-box mt-3" onSubmit={send} noValidate aria-label="Submit audit report">
          <div className="row g-2">
            <div className="col-md-4">
              <label htmlFor="reviewer" className="form-label small">Reviewer</label>
              <input id="reviewer" className={`form-control${submit.fieldError ? " is-invalid" : ""}`} value={reviewer} onChange={(e) => setReviewer(e.target.value)} />
              <div className="invalid-feedback">{submit.fieldError}</div>
            </div>
            <div className="col-md-8">
              <label htmlFor="notes" className="form-label small">Notes (optional)</label>
              <input id="notes" className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          {submit.state === "success" && <div className="alert alert-success py-2 small mt-2 mb-0" role="status">{submit.message}</div>}
          {submit.message && submit.state === "error" && <div className="alert alert-danger py-2 small mt-2 mb-0" role="alert">{submit.message}</div>}
          <button className="btn btn-outline-primary btn-sm mt-2" disabled={submit.state === "submitting"}>
            {submit.state === "submitting" ? "Saving…" : "Save audit report"}
          </button>
        </form>
      )}
    </>
  );
}

export default function ResponsiveAudit() {
  const { width, key } = useBreakpoint();
  const [scenario, setScenario] = useState("");
  const resource = useApiResource(() => apiClient(withScenario("/api/qa/breakpoints", scenario)), [scenario]);

  return <div className="page">
    <ModuleHeader number="025" title="Shared Responsive Breakpoint Audit"
      description="Audit every page against the Bootstrap 5 breakpoint contract: overflow, clipping, tables, card columns and touch targets." />

    <div className="row g-3">
      <div className="col-lg-5">
        <AcceptanceChecklist items={[
          "Breakpoint contract (xs–xxl) is loaded from the API/mock, not hard-coded in the page.",
          "Each page is rendered at each breakpoint width and measured in the real DOM.",
          "No horizontal page scroll; wide tables scroll inside .table-responsive.",
          "Card grids reflow (1 → 2 → 4 columns) as the contract expects.",
          "Results can be saved as an audit report (validated, with API states)."
        ]} />
        <div className="content-card mt-3">
          <div className="section-title">Live viewport</div>
          <div className="d-flex align-items-baseline gap-2">
            <span className="stat-number" data-testid="live-width">{width}px</span>
            <span className="badge-soft badge-info">{key}</span>
          </div>
          <div className="small text-muted mt-1">Resize the window (or use device mode) — this updates live.</div>
        </div>
      </div>

      <div className="col-lg-7">
        <div className="content-card">
          <div className="section-title">Prototype — Breakpoint contract</div>
          <ScenarioSwitcher value={scenario} onChange={setScenario} only={["", "slow", "empty", "error", "unauthorized"]} />
          <div className="mt-3">
            <AsyncBoundary resource={resource}
              emptyProps={{ icon: "bi-arrows-angle-expand", title: "No breakpoints configured", description: "The audit needs at least one breakpoint in the contract." }}>
              {(res) => (
                <>
                  <div className="table-responsive mb-3">
                    <table className="table table-sm mb-0">
                      <thead><tr><th>Key</th><th>Min width</th><th>Test width</th><th>Sidebar</th><th>Stat cards / row</th></tr></thead>
                      <tbody>{res.data.map((b) => (
                        <tr key={b.key} className={b.key === key ? "table-active" : ""}>
                          <td><strong>{b.key}</strong> {b.key === key && <span className="badge-soft badge-success">current</span>}</td>
                          <td>{b.min}px</td><td>{b.testWidth}px</td><td>{b.sidebar}</td><td>{b.statColumns}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  <AuditRunner breakpoints={res.data} />
                </>
              )}
            </AsyncBoundary>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-3">
      <StateMap states={[
        { label: "Load contract", detail: "GET breakpoints", icon: "bi-cloud-download" },
        { label: "Render @ width", detail: "iframe per breakpoint", icon: "bi-arrows-angle-expand" },
        { label: "Measure", detail: "DOM metrics", icon: "bi-rulers" },
        { label: "Report", detail: "POST audit-reports", icon: "bi-clipboard-check" }
      ]} />
    </div>

    <div className="mt-3">
      <ContractPanel
        fields={[
          ["data[].key / min / testWidth", "Breakpoint table + matrix columns", "xs 360, sm 576, md 768, lg 992, xl 1200, xxl 1400"],
          ["data[].sidebar", "Expected sidebar mode", "drawer / rail / full"],
          ["data[].statColumns", "Expected cards per row", "Overview page only"],
          ["POST { reviewer, page, results[], notes }", "Report form", "201 → { reportId, savedAt }"]
        ]}
        navigation={[
          ["Page to audit", "Any module route", "Loaded in an off-screen iframe (same origin)"],
          ["Sidebar audit", "/sidebar-audit", "Drawer/rail behaviour in detail"]
        ]}
        validation={[
          ["Reviewer ≥ 2 characters", "Report form", "“Reviewer name is required (minimum 2 characters).”"],
          ["Results required", "Server 422", "Run the audit before submitting"]
        ]}
        apiStates={[
          ["Loading", "Contract pending", "Skeleton"],
          ["Success", "200 breakpoints[]", "Table + audit runner"],
          ["Empty", "200 []", "“No breakpoints configured”"],
          ["Error", "401 / 5xx / network", "ErrorState"]
        ]}
      />
    </div>
  </div>;
}
